import { LevelState, levelState } from 'components/ApprovalLevelChain';

import { Level, RuleGroup } from './types';

export interface RoundMember {
  level: Level;
  /** This level's own state, which a round's state can outrank. */
  state: LevelState;
}

/**
 * Every level asked at one moment, across every rule that gates the request.
 */
export interface ApprovalRound {
  position: number;
  state: LevelState;
  /** In the server's rule order, then each rule's own level order. */
  members: RoundMember[];
  /** The levels' name when every member carries the same one. */
  name: string | null;
  /** The levels' approver logic when every member shares it. */
  approverLogic: Level['approverLogic'] | null;
}

// Two rules do not move in step, so a round can hold an approved level and an
// open one at once. The round reads as its least settled level.
const ROUND_PRECEDENCE: LevelState[] = [
  'rejected',
  'active',
  'notStarted',
  'cancelled',
  'approved',
];

const shared = <T>(values: T[]): T | null =>
  values.every(value => value === values[0]) ? values[0] : null;

/**
 * The queue's Approval status column, one entry per line.
 *
 * `position` is 1-based within a rule, and each matching rule advances its own
 * ladder, so two rules that each hold a level at position 1 are
 * asked at the same moment. The column reads by that moment rather than by
 * rule: one "Level 1" line, however many rules contribute a level to it.
 *
 * A level whose condition did not match was never part of the request. Nobody
 * was asked and nobody can act, so it is no step in the progress the column
 * reports, and a round it empties goes with it. The review panel's chain still
 * lists it.
 */
export const approvalRounds = (
  rules: RuleGroup[],
  requestSettled: boolean,
): ApprovalRound[] => {
  const byPosition = new Map<number, RoundMember[]>();

  rules.forEach(rule =>
    rule.levels.forEach(level => {
      const state = levelState(level, requestSettled);
      if (state === 'notTriggered') return;

      const members = byPosition.get(level.position) ?? [];
      members.push({ level, state });
      byPosition.set(level.position, members);
    }),
  );

  return Array.from(byPosition.entries())
    .sort(([a], [b]) => a - b)
    .map(([position, members]) => {
      const states = members.map(member => member.state);

      return {
        position,
        state:
          ROUND_PRECEDENCE.find(state => states.includes(state)) ?? states[0],
        members,
        // Two rules asked at once carry two names, and neither names the
        // round. An unnamed level never makes the shared name either.
        name: shared(members.map(member => member.level.name)),
        approverLogic: shared(
          members.map(member => member.level.approverLogic),
        ),
      };
    });
};
